/**
 * CrossStateGuides — internal-linking block shown on Texas + Florida articles.
 * Maps the current article's topic to its equivalent guide in California,
 * New York, and the OTHER enhanced state. This boosts the topical authority
 * of the TX/FL cluster by feeding link equity from / to the existing
 * California/New York pages without changing CA/NY content.
 *
 * The mapping uses a stable "topic key" so we can link e.g. a Texas
 * odometer article → the California, NY and Florida odometer articles.
 */
import { Link } from "react-router-dom";
import { Compass, ArrowUpRight } from "lucide-react";

// Topic key → list of {state, slug, label}
const TOPIC_MAP = {
  checklist: [
    { state: "California", slug: "california-car-bill-of-sale/private-sale-checklist", label: "California private sale checklist" },
    { state: "New York", slug: "new-york-car-bill-of-sale/private-sale-checklist", label: "New York private sale checklist" },
    { state: "Texas", slug: "texas-car-bill-of-sale/private-sale-checklist", label: "Texas private sale checklist" },
    { state: "Florida", slug: "florida-car-bill-of-sale/private-sale-checklist", label: "Florida private sale checklist" },
  ],
  whatToInclude: [
    { state: "California", slug: "california-car-bill-of-sale/what-to-include", label: "What to include — California" },
    { state: "New York", slug: "new-york-car-bill-of-sale/what-to-include", label: "MV-912 fields — New York" },
    { state: "Texas", slug: "texas-car-bill-of-sale/what-to-include", label: "What to include — Texas" },
    { state: "Florida", slug: "florida-car-bill-of-sale/what-to-include", label: "What to include — Florida" },
  ],
  odometer: [
    { state: "California", slug: "california-car-bill-of-sale/odometer-disclosure", label: "Odometer disclosure — California" },
    { state: "New York", slug: "new-york-car-bill-of-sale/odometer-disclosure", label: "Odometer disclosure — New York" },
    { state: "Texas", slug: "texas-car-bill-of-sale/odometer-disclosure", label: "Odometer disclosure — Texas" },
    { state: "Florida", slug: "florida-car-bill-of-sale/odometer-disclosure", label: "Odometer disclosure — Florida" },
  ],
  titleTransfer: [
    { state: "California", slug: "california-car-bill-of-sale/title-transfer", label: "Title transfer — California" },
    { state: "New York", slug: "new-york-car-bill-of-sale/title-transfer-mv82", label: "MV-82 title transfer — New York" },
    { state: "Texas", slug: "texas-car-bill-of-sale/title-transfer-130u", label: "Form 130-U title transfer — Texas" },
    { state: "Florida", slug: "florida-car-bill-of-sale/title-transfer-82040", label: "HSMV 82040 title transfer — Florida" },
  ],
  salesTax: [
    { state: "New York", slug: "new-york-car-bill-of-sale/sales-tax-dtf802", label: "Sales tax DTF-802 — New York" },
    { state: "Texas", slug: "texas-car-bill-of-sale/sales-tax-spv", label: "Sales tax + SPV — Texas" },
    { state: "Florida", slug: "florida-car-bill-of-sale/sales-tax-discretionary-surtax", label: "Sales tax + surtax — Florida" },
  ],
  liabilityRelease: [
    { state: "California", slug: "california-car-bill-of-sale/release-of-liability", label: "Release of liability — California" },
    { state: "Texas", slug: "texas-car-bill-of-sale/vtr-346-release", label: "VTR-346 release — Texas" },
    { state: "Florida", slug: "florida-car-bill-of-sale/notice-of-sale-82050", label: "HSMV 82050 Notice of Sale — Florida" },
  ],
};

// Article slug → topic key.
const SLUG_TO_TOPIC = {
  // Shared across CA/NY/TX/FL
  "private-sale-checklist": "checklist",
  "what-to-include": "whatToInclude",
  "odometer-disclosure": "odometer",
  // California
  "title-transfer": "titleTransfer",
  "release-of-liability": "liabilityRelease",
  // New York
  "title-transfer-mv82": "titleTransfer",
  "sales-tax-dtf802": "salesTax",
  // Texas
  "title-transfer-130u": "titleTransfer",
  "sales-tax-spv": "salesTax",
  "vtr-346-release": "liabilityRelease",
  // Florida
  "notice-of-sale-82050": "liabilityRelease",
  "title-transfer-82040": "titleTransfer",
  "sales-tax-discretionary-surtax": "salesTax",
};

export default function CrossStateGuides({ currentStateSlug, currentArticleSlug }) {
  const topic = SLUG_TO_TOPIC[currentArticleSlug];
  if (!topic) return null;
  const items = (TOPIC_MAP[topic] || []).filter(
    // Exclude the article we're already on (compare by basePath prefix).
    (i) => !i.slug.startsWith(`${currentStateSlug}-car-bill-of-sale/`),
  );
  if (items.length === 0) return null;

  return (
    <section
      data-testid="cross-state-guides"
      className="mt-12 pt-8 border-t border-[var(--csd-border)]"
    >
      <div className="flex items-center gap-2 mb-3 text-[var(--csd-primary)]">
        <Compass size={16} />
        <span className="text-xs uppercase tracking-widest font-semibold">Compare across states</span>
      </div>
      <h2 className="font-heading text-xl font-bold mb-4">
        How the same topic works in other states
      </h2>
      <p className="text-sm text-[var(--csd-text-secondary)] mb-5 max-w-2xl">
        Selling across state lines? Compare how this part of the process changes in California, New York, Texas and Florida.
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {items.map((it) => (
          <li key={it.slug}>
            <Link
              to={`/${it.slug}`}
              data-testid={`cross-state-${it.slug.replace(/\//g, '-')}`}
              className="group flex items-start gap-2 p-3 rounded-lg border border-[var(--csd-border)] hover:border-[var(--csd-primary-light)] hover:bg-[var(--csd-primary-light)]/10 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--csd-text-secondary)] mb-0.5">
                  {it.state}
                </div>
                <div className="text-sm font-semibold text-gray-900 group-hover:text-[var(--csd-primary)] transition-colors">
                  {it.label}
                </div>
              </div>
              <ArrowUpRight size={14} className="text-[var(--csd-text-secondary)] group-hover:text-[var(--csd-primary)] shrink-0 mt-0.5" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
